"use client";

import React, { useEffect, useRef } from "react";
import {
  Avatar,
  Button,
  ModalBody,
  Modal,
  ModalContent,
  ModalHeader,
  Tooltip,
  useDisclosure,
  Textarea,
  Link,
  Image,
} from "@heroui/react";
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownSection,
  DropdownTrigger,
} from "@heroui/dropdown";
// import { Kbd } from "@heroui/kbd";
// import { Input } from "@heroui/input";
import { Icon } from "@iconify/react";
import { cn } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth, useUser, usePermissions } from "@webuild-ai/react-auth";
import { useBrand } from "@webuild-ai/react-auth";

import { useWebuildAPIClient } from "@/providers/WeBuildClientProvider";
import { availableLanguages } from "@/i18n";
import { useTheme } from "@/hooks/use-theme";
import { useGlobalStore } from "@/stores/GlobalStore";
import { useChatStore } from "@/stores/ChatStore";
import { useChatHistoryStore } from "@/stores/ChatHistoryStore";
import { useChatURLState } from "@/hooks/useChatURLState";
import { logger } from "@/telemetry";

import { I18nIcon, LanguageSwitch } from "../language-switch";
import { ChatHistoryComponent } from "../Chat/ChatHistory";
import { SearchChatButton } from "../Chat/SearchChatButton";
import { ChatHistorySearchModal } from "../Chat/ChatHistorySearchModal";
import { MoonFilledIcon, SunFilledIcon, ModelInfoIcon } from "../icons";

import { useSidebarMenuItems } from "./SidebarMenuItems";
import Sidebar from "./Sidebar";
import { useSidebar } from "./SidebarContext";

// import { SearchIcon } from "@/components/icons";

export function SidebarLayoutInvestAI({ children }: { children: React.ReactNode }) {
  const { userProfile } = useUser();
  const { permissions } = usePermissions();
  const { newChat, load, triggerResetPrompt } = useChatStore();
  const {
    fetchChatHistory,
    displayedChats,
    chatHistory,
    selectChat,
    selectAndEnsureVisible,
    clearSelection,
  } = useChatHistoryStore();
  const { user, isAuthenticated } = useAuth();
  const { adminClient } = useWebuildAPIClient();
  const { sidebar } = useGlobalStore();
  const { isSidebarCompact, toggleSidebarCompact } = sidebar;
  const { t } = useTranslation();
  const { logout } = useAuth();
  const location = useLocation();
  const pathname = location.pathname;
  const currentPath = pathname.split("/")?.[1];
  const navigate = useNavigate();
  const { showChatHistory, setShowChatHistory } = useSidebar();
  const { navigateToChat, clearChatId, chatId: chatIdFromURL } = useChatURLState();
  const { workflowId: urlWorkflowId } = useParams<{ workflowId?: string }>();

  // Determine the current workflow ID from URL or default to chat_agent
  const currentWorkflowId = urlWorkflowId || "chat_agent";

  useEffect(() => {
    const shouldShowHistory = location.pathname.startsWith("/chat");

    if (shouldShowHistory !== showChatHistory) {
      setShowChatHistory(shouldShowHistory);
    }
  }, [location.pathname, showChatHistory, setShowChatHistory]);

  // Track the last chat ID we handled from URL to avoid re-processing on displayedChats changes
  const lastHandledUrlChatIdRef = useRef<string | null>(null);

  // Select chat from URL when chat history is loaded (initial page load only)
  useEffect(() => {
    // Skip if no chat ID in URL or already handled this chat ID
    if (!chatIdFromURL || !adminClient || displayedChats.length === 0) {
      return;
    }

    // Skip if we already handled this chat ID (prevents re-running on displayedChats changes)
    if (lastHandledUrlChatIdRef.current === chatIdFromURL) {
      return;
    }

    logger.info("🔗 SidebarLayout: URL has chat ID, selecting and loading", { chatIdFromURL });
    lastHandledUrlChatIdRef.current = chatIdFromURL;

    // Use selectAndEnsureVisible to fetch the chat if it's not in the displayed list
    selectAndEnsureVisible(chatIdFromURL, adminClient);

    // Load the chat data if it's not already loaded
    load(adminClient, chatIdFromURL);
  }, [chatIdFromURL, displayedChats.length, selectAndEnsureVisible, adminClient, load]);

  const handleSelectChat = async (chatHistoryId: string) => {
    if (!adminClient) return;

    // Mark this chat ID as handled BEFORE navigation to prevent URL effect from re-processing
    lastHandledUrlChatIdRef.current = chatHistoryId;

    try {
      // Validate chat exists in history
      const isValid = selectChat(chatHistoryId);

      if (!isValid) {
        logger.error("Chat not found in history");

        return;
      }

      // Navigate to chat URL (IMMEDIATE - user requirement)
      navigateToChat(chatHistoryId, currentWorkflowId);

      // Load chat data from backend
      await load(adminClient, chatHistoryId);
      triggerResetPrompt();
    } catch (error: any) {
      logger.error("Failed to load chat", error);

      // Handle specific HTTP error codes
      if (error?.status === 403) {
        logger.error("You don't have access to this chat");
      } else if (error?.status === 404) {
        logger.error("Chat not found or has been deleted");
      } else {
        logger.error("Failed to load chat. Please try again.");
      }

      // Navigate back to base route on error
      const basePath =
        currentWorkflowId === "chat_agent" ? "/chat" : `/workflows/${currentWorkflowId}`;

      navigate(basePath, { replace: true });
    }
  };

  /**
   * Handler for search modal selections.
   * Unlike handleSelectChat, this uses selectAndEnsureVisible to handle
   * chats that may not be in the local cache (search results from API).
   */
  const handleSearchSelectChat = async (chatHistoryId: string) => {
    if (!adminClient) return;

    // Mark this chat ID as handled BEFORE navigation to prevent URL effect from re-processing
    lastHandledUrlChatIdRef.current = chatHistoryId;

    try {
      // Use selectAndEnsureVisible to fetch the chat if it's not in the displayed list
      // This is necessary for search results which may include chats not in local cache
      await selectAndEnsureVisible(chatHistoryId, adminClient);

      // Navigate to chat URL (IMMEDIATE - user requirement)
      navigateToChat(chatHistoryId, currentWorkflowId);

      // Load chat data from backend
      await load(adminClient, chatHistoryId);
      triggerResetPrompt();
    } catch (error: any) {
      logger.error("Failed to load chat from search", error);

      // Handle specific HTTP error codes
      if (error?.status === 403) {
        logger.error("You don't have access to this chat");
      } else if (error?.status === 404) {
        logger.error("Chat not found or has been deleted");
      } else {
        logger.error("Failed to load chat. Please try again.");
      }

      // Navigate back to base route on error
      const basePath =
        currentWorkflowId === "chat_agent" ? "/chat" : `/workflows/${currentWorkflowId}`;

      navigate(basePath, { replace: true });
    }
  };

  const handleNewChat = () => {
    logger.info("🆕 handleNewChat: Starting new chat");

    // Clear the chat ID from URL first
    clearChatId();
    logger.info("🆕 handleNewChat: URL cleared");

    // Create a new chat session
    newChat();
    logger.info("🆕 handleNewChat: newChat() called");

    // Clear the chat history sidebar selection
    clearSelection();
    logger.info("🆕 handleNewChat: Selection cleared");

    triggerResetPrompt();

    // Focus the chat input field after a short delay
    setTimeout(() => {
      const textarea = document.querySelector(
        'textarea[placeholder*="Start new chat"]',
      ) as HTMLTextAreaElement;

      if (textarea) {
        textarea.focus();
        logger.info("🆕 handleNewChat: Chat input focused");
      }
    }, 150);

    // Fetch chat history again to refresh the list (after clearing selection)
    // Use setTimeout to ensure clearSelection() completes first
    if (adminClient && user?.id) {
      setTimeout(() => {
        logger.info("🆕 handleNewChat: Fetching chat history after clear");
        fetchChatHistory(user.id, adminClient);
      }, 10); // Increased from 0 to 10ms
    }
  };

  // const searchInput = (
  //   <Input
  //     aria-label={t("search")}
  //     classNames={{
  //       inputWrapper: "bg-default-100",
  //       input: "text-sm",
  //     }}
  //     data-id="search-input"
  //     endContent={
  //       <Kbd className="hidden lg:inline-block" keys={["command"]}>
  //         K
  //       </Kbd>
  //     }
  //     labelPlacement="outside"
  //     placeholder={`${t("search")}…`}
  //     startContent={
  //       <SearchIcon className="text-base text-default-400 pointer-events-none flex-shrink-0" />
  //     }
  //     type="search"
  //   />
  // );

  const { menuItems: sidebarMenuItems } = useSidebarMenuItems();
  const {
    isOpen: isModalOpen,
    onOpen: onModalOpen,
    onOpenChange: onModalOpenChange,
  } = useDisclosure();
  const {
    isOpen: isHelpModalOpen,
    onOpen: onHelpModalOpen,
    onOpenChange: onHelpModalOpenChange,
  } = useDisclosure();
  const {
    isOpen: isSearchModalOpen,
    onOpen: onSearchModalOpen,
    onOpenChange: onSearchModalOpenChange,
  } = useDisclosure();

  // Keyboard shortcut: Cmd+K (Mac) / Ctrl+K (Windows/Linux) to open search
  useEffect(() => {
    const handleKeyDown = (e: globalThis.KeyboardEvent) => {
      logger.info("SidebarLayout-InvestAI keydown", {
        key: e.key,
        metaKey: String(e.metaKey),
        ctrlKey: String(e.ctrlKey),
        showChatHistory: String(showChatHistory),
        displayedChatsLength: displayedChats.length,
        activeElement: document.activeElement?.tagName,
      });

      // Check for Cmd+K (Mac) or Ctrl+K (Windows/Linux)
      // Check both lowercase and uppercase 'k'/'K'
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        logger.info("🔍 Ctrl/Cmd+K detected, checking conditions", {
          showChatHistory: String(showChatHistory),
          displayedChatsLength: displayedChats.length,
          shouldOpen: String(showChatHistory && displayedChats.length > 0),
        });

        // Only trigger if on chat page with history visible
        if (showChatHistory && displayedChats.length > 0) {
          e.preventDefault(); // Prevent browser default (Chrome address bar focus)
          e.stopPropagation();
          logger.info("✅ Opening search modal via keyboard shortcut");
          onSearchModalOpen();
        }
      }
    };

    // Use capture phase to intercept events before they reach child elements
    document.addEventListener("keydown", handleKeyDown, true);

    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [showChatHistory, displayedChats.length, onSearchModalOpen]);

  const { brand } = useBrand();
  const { theme, toggleTheme, isDark } = useTheme();

  const BrandIcon = theme === "dark" && brand.iconDark ? brand.iconDark : brand.icon;
  const SmallBrandIcon =
    theme === "dark" && brand.smallIconDark ? brand.smallIconDark : brand.smallIcon || BrandIcon;
  const BrandLogo = theme === "dark" && brand.logoDark ? brand.logoDark : brand.logo;

  const handleFeedbackSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const feedback = formData.get("feedback") as string;

    try {
      if (!adminClient) {
        throw new Error("Admin client not found");
      }
      await adminClient.feedback.createFeedback({
        feedback,
      });
      onModalOpenChange();
    } catch (error) {
      logger.error("Failed to submit feedback", error);
    }
  };

  return (
    <div className="flex h-dvh w-full">
      {isAuthenticated && brand.name && userProfile?.name && (
        <div
          className={cn(
            "relative flex h-full w-65 flex-col !border-r-small border-divider transition-width duration-150 ease-in-out light:bg-brand-secondary",
            {
              "w-16 items-center px-2": isSidebarCompact,
            },
            theme === "light" && "bg-brand-secondary",
          )}
          style={{
            transitionProperty: "width",
            willChange: "width",
            borderColor: "var(--brand-tertiary) !important",
          }}
        >
          {/* Sidebar header: logo and toggle button with smooth transition */}
          {isSidebarCompact ? (
            // Collapsed: logo on top, toggle button below
            <div className="flex flex-col items-center pt-4 gap-2">
              {/* Logo */}
              <Link
                aria-label="Home"
                className="flex items-center justify-center"
                color="foreground"
                href="/"
              >
                <SmallBrandIcon size={24} />
              </Link>
              {/* Toggle Button */}
              <Tooltip showArrow content={t("Open sidebar")} placement="right">
                <Button
                  isIconOnly
                  aria-label="Expand sidebar"
                  className="!m-0"
                  size="sm"
                  variant="light"
                  onPress={toggleSidebarCompact}
                >
                  <Icon
                    color="#8f8f8f"
                    height={20}
                    icon="solar:sidebar-minimalistic-linear"
                    width={20}
                  />
                </Button>
              </Tooltip>
            </div>
          ) : (
            // Expanded: logo left, toggle right
            <div className="flex items-center justify-between p-4 w-full">
              <Link aria-label="Home" className="flex items-center p-2" color="foreground" href="/">
                {/* <SmallBrandIcon size={24} /> */}
                <BrandLogo className="mx-auto" size={40} />
              </Link>
              <Tooltip showArrow content={t("Close sidebar")} placement="bottom">
                <Icon
                  color="#8f8f8f"
                  height={24}
                  icon="solar:sidebar-minimalistic-line-duotone"
                  style={{ cursor: "pointer" }}
                  width={24}
                  onClick={toggleSidebarCompact}
                />
              </Tooltip>
            </div>
          )}
          {/* End sidebar header */}
          <Button
            className={cn("btn-brand-primary text-sm mx-4 h-10 mb-2", {
              "mt-6": isSidebarCompact,
            })}
            data-id="new-chat-button"
            isIconOnly={isSidebarCompact}
            size="md"
            startContent={
              <Image
                height={18}
                radius="none"
                src={`/brands/ii/pathfinder.svg`}
                style={{ filter: "invert(1) brightness(2)" }}
              />
            }
            onPress={handleNewChat}
          >
            {!isSidebarCompact && t("New Chat")}
          </Button>
          {/* Main sidebar content: Sidebar + ChatHistory, scrollable */}
          <div className="flex-1 flex flex-col min-h-0">
            {/* <div> */}
            <Sidebar
              className="px-4 py-2"
              defaultSelectedKey="home"
              isCompact={isSidebarCompact}
              itemClasses={{
                base: !isSidebarCompact ? "mb-2" : "",
                title: "text-brand-primary font-bold dark:!text-foreground",
              }}
              items={sidebarMenuItems}
              selectedKeys={[currentPath]}
              showMenuTitle={false}
            />
            {isSidebarCompact && showChatHistory && displayedChats.length > 0 && (
              <Tooltip content={t("Search chat history")} placement="right">
                <Button
                  isIconOnly
                  aria-label={t("Search chat history")}
                  className="btn-brand-primary mx-4 h-10 mb-2"
                  data-id="compact-search-button"
                  size="md"
                  onPress={onSearchModalOpen}
                >
                  <Icon className="text-brand-secondary" icon="solar:magnifer-linear" width={20} />
                </Button>
              </Tooltip>
            )}
            {!isSidebarCompact && showChatHistory && (
              <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
                <div className="flex items-center justify-between px-[30px] py-2 flex-shrink-0">
                  <span className="font-bold text-brand-primary text-sm dark:!text-foreground">
                    {t("Chat History")}
                  </span>
                  <SearchChatButton
                    isCompact={true}
                    isDisabled={displayedChats.length === 0}
                    onOpenSearch={onSearchModalOpen}
                  />
                </div>
                <div className="flex-1 min-h-0">
                  <ChatHistoryComponent
                    isShowHistory={true}
                    sectionClasses={{
                      base: "px-4",
                    }}
                    showScroll={true}
                    onNewChat={handleNewChat}
                    onSelectChat={handleSelectChat}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Footer always at the bottom */}
          <div
            className={cn("mt-auto flex flex-col flex-shrink-0", {
              "items-center": isSidebarCompact,
            })}
          >
            {userProfile?.name && (
              <div className="flex flex-col gap-4 mt-3 mb-2 px-4" data-id="user-profile-container">
                <Dropdown data-id="user-dropdown" placement="top-end">
                  <DropdownTrigger>
                    <Button
                      className={`btn-brand-primary text-sm h-10 ${isSidebarCompact ? "justify-center" : "justify-start"}`}
                      data-id="user-dropdown-button"
                      isIconOnly={isSidebarCompact}
                      size="md"
                      startContent={
                        <Avatar
                          className="flex-shrink-0 w-5 h-5 text-tiny"
                          data-id="user-avatar"
                          src={userProfile?.picture}
                        />
                      }
                    >
                      {!isSidebarCompact && (
                        <span className="text-brand-secondary break-words" data-id="user-name">
                          {userProfile?.name}
                        </span>
                      )}
                    </Button>
                  </DropdownTrigger>
                  <DropdownMenu
                    aria-label="Profile Actions"
                    className="w-[210px] bg-content1 px-[8px] py-[8px]"
                    data-id="profile-dropdown-menu"
                    variant="flat"
                  >
                    <DropdownSection
                      aria-label="profile-section-3"
                      className="mb-0"
                      data-id="dropdown-section-3"
                    >
                      <DropdownItem
                        key="theme-switch"
                        className="pt-[4px] text-default-500"
                        closeOnSelect={false}
                        data-id="dropdown-item-theme-switch"
                        textValue="Theme Switch"
                        onPress={() => {
                          toggleTheme();
                        }}
                      >
                        <Tooltip content="Theme Switch" placement="right">
                          {/* <ThemeSwitch data-id="theme-switch" /> */}
                          {theme !== "dark" ? (
                            <div className="flex items-center gap-2">
                              <MoonFilledIcon size={20} />
                              <span className="text-small font-medium text-default-500">
                                {t("Dark Mode")}
                              </span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <SunFilledIcon size={20} />
                              <span className="text-small font-medium text-default-500">
                                {t("Light Mode")}
                              </span>
                            </div>
                          )}
                        </Tooltip>
                      </DropdownItem>
                      <DropdownItem
                        key="model-info"
                        className="py-[4px] text-default-500"
                        data-id="dropdown-item-model-info"
                        textValue="Models Overview"
                        onPress={() => navigate("/models")}
                      >
                        <div className="flex items-center gap-2">
                          <ModelInfoIcon size={20} />
                          <span className="text-small font-medium text-default-500">
                            {t("Models Overview")}
                          </span>
                        </div>
                      </DropdownItem>

                      <DropdownItem
                        key="settings"
                        className={permissions.settings ? "py-[4px] text-default-500" : "hidden"}
                        data-id="dropdown-item-settings"
                        textValue="Settings"
                        onPress={() => permissions.settings && navigate("/settings")}
                      >
                        <div className="flex items-center gap-2">
                          <Icon icon="solar:settings-outline" width={20} />
                          <span className="text-small font-medium text-default-500">
                            {t("Settings")}
                          </span>
                        </div>
                      </DropdownItem>
                      <DropdownItem
                        key="logout"
                        className="pt-[4px] text-default-500"
                        data-id="dropdown-item-logout"
                        textValue="Log Out"
                        onPress={() => logout()}
                      >
                        <div className="flex items-center gap-2">
                          <Icon icon="solar:logout-3-outline" width={20} />
                          <span className="text-small font-medium text-default-500">
                            {t("Log Out")}
                          </span>
                        </div>
                      </DropdownItem>
                    </DropdownSection>
                  </DropdownMenu>
                </Dropdown>
              </div>
            )}
            <div className="px-4">
              <LanguageSwitch
                availableLanguages={availableLanguages}
                data-id="language-switch"
                icon={I18nIcon}
                isSidebarCompact={isSidebarCompact}
              />
            </div>
            {brand.showNeedHelp && (
              <Tooltip
                content={`${t("Need Help")}?`}
                isDisabled={!isSidebarCompact}
                placement="right"
              >
                <Button
                  className={`btn-brand-primary text-sm mx-4 my-2 h-10 ${isSidebarCompact ? "justify-center" : "justify-start"}`}
                  data-id="need-help-button"
                  isIconOnly={isSidebarCompact}
                  size="md"
                  startContent={
                    <Icon
                      className="flex-none text-brand-secondary"
                      icon="solar:call-chat-rounded-linear"
                      width={20}
                    />
                  }
                  onPress={onHelpModalOpen}
                >
                  {!isSidebarCompact && `${t("Need Help")}?`}
                </Button>
              </Tooltip>
            )}
            <Tooltip content={t("Feedback")} isDisabled={!isSidebarCompact} placement="right">
              <Button
                className={`btn-brand-primary text-sm mx-4 h-10 ${isSidebarCompact ? "justify-center" : "justify-start"}`}
                data-id="feedback-button"
                isIconOnly={isSidebarCompact}
                size="md"
                slot="start"
                startContent={
                  <Icon
                    className="flex-none text-default-500 text-brand-secondary"
                    icon="solar:question-circle-linear"
                    width={20}
                  />
                }
                onPress={onModalOpen}
              >
                {!isSidebarCompact && t("Feedback")}
              </Button>
            </Tooltip>

            <Modal isOpen={isModalOpen} size="4xl" onOpenChange={onModalOpenChange}>
              <ModalContent>
                {(onClose: any) => (
                  <>
                    <ModalHeader className="flex flex-col gap-1 font-bold text-brand-primary dark:!text-white">
                      {t("Feedback")}
                    </ModalHeader>
                    <ModalBody>
                      <form onSubmit={handleFeedbackSubmit}>
                        <Textarea
                          aria-label="Feedback input"
                          className="w-full"
                          label={t("Your Feedback")}
                          minRows={3}
                          name="feedback"
                          placeholder={t("Enter your feedback here...")}
                        />
                        <div className="flex justify-end gap-2 mt-4">
                          <Button
                            aria-label="Close feedback modal"
                            style={{
                              backgroundColor: isDark
                                ? brand.colors.dark.tertiary
                                : brand.colors.light.tertiary,
                              color: "white",
                            }}
                            variant="solid"
                            onPress={onClose}
                          >
                            {t("Close")}
                          </Button>
                          <Button
                            aria-label="Submit feedback"
                            className="btn-brand-primary text-sm"
                            type="submit"
                          >
                            {t("Submit")}
                          </Button>
                        </div>
                      </form>
                    </ModalBody>
                  </>
                )}
              </ModalContent>
            </Modal>

            <Modal isOpen={isHelpModalOpen} size="md" onOpenChange={onHelpModalOpenChange}>
              <ModalContent>
                <ModalHeader className="flex flex-col gap-1 font-bold text-brand-primary dark:!text-white">
                  {t("Need Help?")}
                </ModalHeader>
                <ModalBody>
                  <div className="flex flex-col gap-4 text-base text-default-700">
                    <div className="text-default-700">
                      {t(
                        "If you need assistance, our support team is here to help you. You can reach us through the following channel:",
                      )}
                    </div>
                    <div className="flex flex-col gap-2 items-center">
                      <Button
                        as="a"
                        className="btn-brand-primary text-sm"
                        href={brand.helpUrl}
                        rel="noopener noreferrer"
                        startContent={
                          <Icon height={22} icon="solar:call-chat-rounded-linear" width={22} />
                        }
                        target="_blank"
                      >
                        {t("Live Chat with InvestAI Support")}
                      </Button>
                      <div className="text-xs text-default-500 text-center mt-2">
                        {t("We are available and happy to assist you!")}
                      </div>
                    </div>
                  </div>
                </ModalBody>
              </ModalContent>
            </Modal>
          </div>
        </div>
      )}

      {/* Chat History Search Modal */}
      <ChatHistorySearchModal
        chatHistory={chatHistory}
        isOpen={isSearchModalOpen}
        onOpenChange={onSearchModalOpenChange}
        onSelectChat={handleSearchSelectChat}
      />

      <div className="w-full flex-1 flex-col overflow-hidden">
        <main className="h-full w-full overflow-auto">{children}</main>
      </div>
    </div>
  );
}
